<template>
  <v-container class="relative container1400">
    <v-alert dense outlined type="error" v-if="error">
      <ul>
        <li v-for="item in error" :key="item.msg">{{ $t(item.msg) }}</li>
      </ul>
    </v-alert>
    <v-form
      ref="form"
      class="pb-5"
      v-model="valid"
      @submit.prevent="next"
      lazy-validation
    >
      <h2 class="text-h5 mt-6 mb-6 font-weight-regular text-left">
        {{ $t("Houserules") }}
      </h2>
      <v-card class="pa-7">
        <v-row v-if="rulesList">
          <v-col>
            <v-combobox
              v-model="data.rules"
              :items="rulesList"
              :label="$t(`Restrictions`)"
              multiple
              outlined
              chips
            >
              <template v-slot:selection="data">
                <v-chip class="ma-1">
                  {{ $t(data.item) }}
                </v-chip>
              </template>
              <template slot="item" slot-scope="data">
                {{ $t(data.item) }}
              </template>
            </v-combobox>
          </v-col>
          <v-col>
            <v-select
              v-model="data.age_restriction"
              :items="age_restriction"
              :label="$t(`s1655921125`)"
              outlined
              chips
            >
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-title
                    :id="attrs['age-restrictions']"
                    v-text="item"
                  ></v-list-item-title>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <div class="overline pb-2">{{ $t("Cmsg16") }}</div>
        <v-row>
          <v-col cols="12" md="4">
            <v-combobox
              v-model="data.check_in_times"
              :items="times"
              :label="$t(`CheckInTimes`)"
              multiple
              outlined
              chips
            >
              <template v-slot:selection="data">
                <v-chip dark color="light-blue" class="mt-2 mb-2">
                  {{ data.item }}
                </v-chip>
              </template>
            </v-combobox>
          </v-col>
          <v-col cols="12" md="4">
            <v-combobox
              v-model="data.check_out_times"
              :items="times"
              :label="$t(`CheckOutTimes`)"
              multiple
              outlined
              chips
            >
              <template v-slot:selection="data">
                <v-chip dark color="light-blue" class="mt-2 mb-2">
                  {{ data.item }}
                </v-chip>
              </template>
            </v-combobox>
          </v-col>
        </v-row>
      </v-card>
      <h2 class="text-h5 mt-6 mb-6 font-weight-regular text-left">
        {{ $t("Baseprice") }}
      </h2>
      <v-card class="pa-7">
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              type="text"
              v-model="data.price.daily"
              :suffix="data.price.currencyCode"
              :rules="numberRule"
              v-bind:label="$t('Dailyprice')"
              placeholder="Ex:120"
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              outlined
              :items="currencies"
              item-value="code"
              item-text="code"
              v-model="data.price.currencyCode"
              :label="$t('Currency')"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              type="text"
              v-model="data.price.weekly"
              :suffix="data.price.currencyCode"
              :rules="numberRule"
              v-bind:label="$t('Weeklyprice')"
              placeholder="Ex:820"
              v-bind:hint="$t('PM3')"
              persistent-hint
              outlined
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <v-alert dense type="info">
              <a
                class="white--text"
                href="https://member.nomadrooms.com/checkout/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ $t("s1656699105") }}</a
              >
            </v-alert>
            <v-text-field
              type="text"
              v-model="data.price.monthly"
              :suffix="data.price.currencyCode"
              :rules="numberRule"
              v-bind:label="$t('Monthlyprice')"
              placeholder="Ex:3300"
              v-bind:hint="$t('PM4')"
              persistent-hint
              outlined
            ></v-text-field>
            <div v-if="data.price.monthly">
              <div class="overline pb-0 grey--text">
                {{ $t("SecurityDeposittobeCollected") }}
              </div>
              <div class="text-h6">
                {{ data.price.currencyCode }}
                {{ data.price.monthly }} ({{ $t("Morethan6months") }})
              </div>
              <div class="text-h6">
                {{ data.price.currencyCode }}
                {{ data.price.monthly / 2 }} ({{ $t("Lessthan6months") }})
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="3"> </v-col>
        </v-row>
      </v-card>
      <h2 class="text-h5 mt-6 mb-6 font-weight-regular text-left">
        {{ $t("PM5") }}
      </h2>
      <v-card class="pa-7">
        <v-row>
          <v-col cols="12" md="4">
            <v-select
              :label="$t('PM6')"
              v-bind:hint="$t('PM8')"
              v-model="data.short_term_buffer"
              :items="short_bf"
              item-text="text"
              item-value="abbr"
              :rules="reqRules"
              persistent-hint
              outlined
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              :label="$t('PM7')"
              v-bind:hint="$t('PM8')"
              v-model="data.long_term_buffer"
              :items="long_bf"
              item-text="text"
              item-value="abbr"
              :rules="reqRules"
              persistent-hint
              outlined
            ></v-select>
          </v-col>
        </v-row>
      </v-card>
      <div class="text-right mt-10">
        <v-btn
          outlined
          color="grey"
          large
          class="mr-2"
          @click="$emit('goBack')"
          >{{ $t("Back") }}</v-btn
        >
        <v-btn large color="light-blue white--text" rounded type="submit">
          <span class="mr-2">{{ $t("SaveNContinue") }}</span>
          <v-icon size="small">mdi-arrow-right-thick</v-icon>
        </v-btn>
      </div>
    </v-form>
  </v-container>
</template>

<script>
import ListingsData from "@/assets/listings.json";
import ListingService from "@/services/ListingService";
import Currencies from "@/assets/currencies.json";

export default {
  props: ["data", "rulesList"],
  name: "Step7Component",
  components: {},
  data() {
    return {
      valid: null,
      error: null,
      currencies: Currencies,
      short_bf: ListingsData.short_bf,
      long_bf: ListingsData.long_bf,
      times: ListingsData.times,
      age_restriction: ListingsData.age_restriction,
      reqRules: [value => !!value || this.$t("string719")],
      numberRule: [
        value => !!value || this.$t("string719"),
        value => {
          const pattern = /^\d+(?:\d+)*$/gm;
          return pattern.test(value) || this.$t("string721");
        }
      ]
    };
  },
  methods: {
    async next() {
      await this.$refs.form.validate();
      if (this.valid) {
        this.waitDialog = true;
        await ListingService.propertyAdd(this.data)
          .then(result => {
            this.waitDialog = false;
            const { property_id } = result.data;
            window.scrollTo(0, 0);
            this.data.steps = 8;
            this.data.property_id = property_id;
            this.$store.dispatch("setRole", "Host");
          })
          .catch(err => {
            this.waitDialog = false;
            this.error = err.response.data.errors;
          });
      }
    }
  }
};
</script>

<style scoped>
.ap-input {
  padding-left: 50px;
}
</style>
